import { Box } from '@zoopla-eng/zds/box';
import { ButtonLeft } from '@zoopla-eng/zds/button';
import { SearchMedium } from '@zoopla-eng/zds/icons';
import { Stack } from '@zoopla-eng/zds/stack';
import {
    LocationSearchInput,
    QueryType
} from '@zoopla/listing-search-location-search-input';
import { triggerTracking } from '@zoopla/tracking';
import { useRouter } from 'next/router';
import { useState } from 'react';
import {
    buttonContainerStyle,
    locationSearchInputContainerStyle,
    searchWrapperContainerStyle
} from './HousePricesSearch.styles.css';

const buildSearchUrl = (identifier: string, value: string) => {
    const housePricesPath = '/house-prices/';
    const q = `?q=${encodeURIComponent(value)}`;
    const url = `${housePricesPath}${identifier}${
        identifier ? '/' : ''
    }${q}&search_source=house-prices`;

    return url;
};

type Props = {
    isLandingPage: boolean;
    buttonLabel?: string;
};

export const HousePricesSearch = ({
    buttonLabel = 'Search',
    isLandingPage
}: Props) => {
    const router = useRouter();
    const [error, setError] = useState('');
    const [locationText, setLocationText] = useState<string | undefined>('');
    const [selectedLocation, setSelectedLocation] = useState<{
        id: string;
        text: string;
    } | null>({
        id: '',
        text: ''
    });

    const value = selectedLocation?.text || locationText || '';

    const handleInputValueChange = (inputValue?: string) => {
        if (error && inputValue) setError('');
        setLocationText(inputValue);
    };

    const searchSubmit = (event: React.SyntheticEvent) => {
        event.preventDefault();

        if (!selectedLocation?.text && !locationText) {
            setError('Please enter a valid search area');
            return;
        }

        triggerTracking({
            event: 'GAEvent',
            eventAction: 'click',
            eventCategory: 'search results',
            eventLabel: isLandingPage ? 'house prices' : 'house prices results'
        });

        router.push(buildSearchUrl(selectedLocation?.id || '', value));
    };

    return !isLandingPage ? (
        <form onSubmit={searchSubmit}>
            <Box bpS200={{ px: 0 }} px="S400">
                <Stack flexDirection="row" flexWrap="nowrap">
                    <div className={searchWrapperContainerStyle}>
                        <LocationSearchInput
                            label="Search area"
                            placeholder="e.g. Oxford, Old Kent Road or NW3"
                            queryType={QueryType.AddressPostcodes}
                            onSelectedItemChange={setSelectedLocation}
                            onInputValueChange={handleInputValueChange}
                            errorText={error}
                            initialSelectedItem={{
                                id: (
                                    router.query['search-path'] as string[]
                                )?.join('/'),
                                text: router.query.q as string
                            }}
                        />
                    </div>
                    <div className={buttonContainerStyle}>
                        <ButtonLeft
                            category="primary"
                            Icon={SearchMedium}
                            data-testid="search-btn"
                            fw
                        >
                            {buttonLabel}
                        </ButtonLeft>
                    </div>
                </Stack>
            </Box>
        </form>
    ) : (
        <form onSubmit={searchSubmit}>
            <Stack
                {...{
                    '@S200': {
                        flexDirection: 'column',
                        items: { flexBasis: 0, flexGrow: 1 },
                        alignItems: 'center',
                        width: '100%'
                    }
                }}
                rowGap="S400"
            >
                <div className={locationSearchInputContainerStyle}>
                    <LocationSearchInput
                        label="Search area"
                        placeholder="e.g. Oxford, Old Kent Road or NW3"
                        queryType={QueryType.AddressPostcodes}
                        onSelectedItemChange={setSelectedLocation}
                        onInputValueChange={handleInputValueChange}
                        errorText={error}
                    />
                </div>
                <Box width="100%">
                    <ButtonLeft
                        category="primary"
                        Icon={SearchMedium}
                        data-testid="search-btn"
                        fw
                    >
                        {buttonLabel}
                    </ButtonLeft>
                </Box>
            </Stack>
        </form>
    );
};
